import React, { useState, useEffect } from 'react';
import { Settings, Save, Bell, Clock, CheckCircle2, Wand2, ArrowRight } from 'lucide-react';
import toast from 'react-hot-toast';
import api from '../../utils/api';

const TOKENS = {
    bgCard: '#FFFFFF',
    bgHover: '#F8FAFC',
    border: '#E2E8F0',
    borderLight: '#F1F5F9',
    textPrimary: '#0F172A',
    textSecondary: '#475569',
    textMuted: '#94A3B8',
    accent: '#4F46E5',
    accentLight: '#EEF2FF',
    success: '#10B981',
    successLight: '#DCFCE7',
};

const Toggle = ({ checked, onChange, label }) => (
    <label className="flex items-center cursor-pointer gap-2">
        <div className="relative">
            <input type="checkbox" className="sr-only" checked={checked} onChange={onChange} />
            <div className={`block w-10 h-6 rounded-full transition-colors ${checked ? 'bg-indigo-600' : 'bg-slate-200'}`}></div>
            <div className={`absolute left-1 top-1 bg-white w-4 h-4 rounded-full transition-transform ${checked ? 'transform translate-x-4' : ''}`}></div>
        </div>
        {label && <span className="text-sm font-medium text-slate-700">{label}</span>}
    </label>
);

const NotificationSettingsPanel = ({ analyticsContext }) => {
    const [isExpanded, setIsExpanded] = useState(false);
    const [isLoading, setIsLoading] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [isSuggesting, setIsSuggesting] = useState(false);
    const [isCreatingNotice, setIsCreatingNotice] = useState(false);
    const [suggestion, setSuggestion] = useState(null);

    const [settings, setSettings] = useState({
        overdue_enabled: true,
        overdue_recipients: 'employee_and_admin',
        approaching_enabled: true,
        approaching_threshold_hours: 24,
        approaching_recipients: 'employee_only',
        completion_enabled: true,
        completion_notify_admin: true
    });

    useEffect(() => {
        if (isExpanded) {
            fetchSettings();
        }
    }, [isExpanded]);

    const fetchSettings = async () => {
        setIsLoading(true);
        try {
            const res = await api.get('/analytics/notification-settings');
            if (res.data.success && res.data.settings) {
                setSettings(res.data.settings);
            }
        } catch (err) {
            console.error('Failed to load notification settings:', err);
            toast.error('Failed to load notification settings');
        } finally {
            setIsLoading(false);
        }
    };

    const handleSaveSettings = async () => {
        setIsSaving(true);
        try {
            await api.put('/analytics/notification-settings', settings);
            toast.success('Notification settings saved successfully');
        } catch (err) {
            console.error('Failed to save settings:', err);
            toast.error('Failed to save notification settings');
        } finally {
            setIsSaving(false);
        }
    };

    const handleSuggestReminder = async () => {
        setIsSuggesting(true);
        setSuggestion(null);
        try {
            const res = await api.post('/analytics/suggest-reminder', { analyticsContext });
            if (res.data.success && res.data.suggestion) {
                setSuggestion(res.data.suggestion);
                toast.success('AI reminder generated successfully');
            } else {
                toast.error(res.data.message || 'Failed to generate reminder');
            }
        } catch (err) {
            console.error('AI suggestion error:', err);
            toast.error(err.response?.data?.message || 'Failed to suggest reminder');
        } finally {
            setIsSuggesting(false);
        }
    };

    const handleCreateNotice = async () => {
        if (!suggestion) return;
        setIsCreatingNotice(true);
        try {
            await api.post('/notices', {
                title: suggestion.title,
                content: suggestion.content,
                phase: suggestion.phase || 'active',
                priority: suggestion.priority || 'high',
                assignment_type: 'all',
                status: 'published',
                show_popup_on_login: true
            });
            toast.success('Notice published successfully!');
            setSuggestion(null);
        } catch (err) {
            console.error('Failed to create notice:', err);
            toast.error('Failed to publish notice');
        } finally {
            setIsCreatingNotice(false);
        }
    };

    const updateSetting = (key, value) => {
        setSettings(prev => ({ ...prev, [key]: value }));
    };

    return (
        <div style={{
            margin: '20px 0',
            backgroundColor: TOKENS.bgCard,
            border: `1px solid ${TOKENS.border}`,
            borderRadius: '12px',
            overflow: 'hidden',
            boxShadow: '0 2px 8px rgba(0,0,0,0.02)',
            fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif'
        }}>
            {/* Header / Toggle */}
            <div
                onClick={() => setIsExpanded(!isExpanded)}
                style={{
                    padding: '16px 20px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    cursor: 'pointer',
                    userSelect: 'none',
                    backgroundColor: isExpanded ? TOKENS.bgHover : TOKENS.bgCard,
                    transition: 'background-color 0.2s'
                }}
            >
                <div style={{ display: 'flex', alignItems: 'center', gap: '12px' }}>
                    <div style={{
                        width: '32px', height: '32px', borderRadius: '8px',
                        background: TOKENS.accentLight, color: TOKENS.accent,
                        display: 'flex', alignItems: 'center', justifyContent: 'center'
                    }}>
                        <Settings size={18} />
                    </div>
                    <div>
                        <h3 style={{ margin: 0, fontSize: '15px', fontWeight: 600, color: TOKENS.textPrimary }}>
                            Smart Notifications & Reminders
                        </h3>
                        <p style={{ margin: '2px 0 0 0', fontSize: '12px', color: TOKENS.textSecondary }}>
                            Configure automated alerts and AI-suggested company notices
                        </p>
                    </div>
                </div>
            </div>

            {/* Expanded Content */}
            {isExpanded && (
                <div style={{ padding: '20px', borderTop: `1px solid ${TOKENS.borderLight}` }}>
                    {isLoading ? (
                        <div className="py-8 text-center text-slate-500">Loading settings...</div>
                    ) : (
                        <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                            {/* Left Column: Automation Settings */}
                            <div className="space-y-6">
                                <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Bell size={14} className="text-indigo-500" />
                                    Automation Rules
                                </h4>

                                {/* Overdue Tasks */}
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="font-medium text-slate-800 text-sm">Overdue Tasks</p>
                                            <p className="text-xs text-slate-500 mt-1">Notify when tasks pass their deadline</p>
                                        </div>
                                        <Toggle
                                            checked={settings.overdue_enabled}
                                            onChange={(e) => updateSetting('overdue_enabled', e.target.checked)}
                                        />
                                    </div>
                                    <div className="flex items-center gap-3">
                                        <span className="text-xs text-slate-600 w-20">Recipients:</span>
                                        <select
                                            className="flex-1 text-sm rounded-md border-slate-200 py-1 px-2 disabled:opacity-50"
                                            value={settings.overdue_recipients}
                                            onChange={(e) => updateSetting('overdue_recipients', e.target.value)}
                                            disabled={!settings.overdue_enabled}
                                        >
                                            <option value="employee_only">Employee Only</option>
                                            <option value="admin_only">Admin Only</option>
                                            <option value="employee_and_admin">Employee & Admin</option>
                                        </select>
                                    </div>
                                </div>

                                {/* Approaching Deadlines */}
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="font-medium text-slate-800 text-sm">Approaching Deadlines</p>
                                            <p className="text-xs text-slate-500 mt-1">Notify before tasks become overdue</p>
                                        </div>
                                        <Toggle
                                            checked={settings.approaching_enabled}
                                            onChange={(e) => updateSetting('approaching_enabled', e.target.checked)}
                                        />
                                    </div>
                                    <div className="space-y-2">
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-600 w-20">Threshold:</span>
                                            <select
                                                className="flex-1 text-sm rounded-md border-slate-200 py-1 px-2 disabled:opacity-50"
                                                value={settings.approaching_threshold_hours}
                                                onChange={(e) => updateSetting('approaching_threshold_hours', parseInt(e.target.value))}
                                                disabled={!settings.approaching_enabled}
                                            >
                                                <option value={6}>6 Hours Before</option>
                                                <option value={12}>12 Hours Before</option>
                                                <option value={24}>24 Hours Before</option>
                                                <option value={48}>48 Hours Before</option>
                                                <option value={72}>72 Hours Before</option>
                                            </select>
                                        </div>
                                        <div className="flex items-center gap-3">
                                            <span className="text-xs text-slate-600 w-20">Recipients:</span>
                                            <select
                                                className="flex-1 text-sm rounded-md border-slate-200 py-1 px-2 disabled:opacity-50"
                                                value={settings.approaching_recipients}
                                                onChange={(e) => updateSetting('approaching_recipients', e.target.value)}
                                                disabled={!settings.approaching_enabled}
                                            >
                                                <option value="employee_only">Employee Only</option>
                                                <option value="admin_only">Admin Only</option>
                                                <option value="employee_and_admin">Employee & Admin</option>
                                            </select>
                                        </div>
                                    </div>
                                </div>

                                {/* Completions */}
                                <div className="p-4 rounded-lg bg-slate-50 border border-slate-100">
                                    <div className="flex justify-between items-start mb-3">
                                        <div>
                                            <p className="font-medium text-slate-800 text-sm">Task Completions</p>
                                            <p className="text-xs text-slate-500 mt-1">Admin notifications for completed tasks</p>
                                        </div>
                                        <Toggle
                                            checked={settings.completion_enabled}
                                            onChange={(e) => updateSetting('completion_enabled', e.target.checked)}
                                        />
                                    </div>
                                    <label className="flex items-center gap-2 mt-2 cursor-pointer">
                                        <input
                                            type="checkbox"
                                            className="rounded text-indigo-600 disabled:opacity-50"
                                            checked={settings.completion_notify_admin}
                                            onChange={(e) => updateSetting('completion_notify_admin', e.target.checked)}
                                            disabled={!settings.completion_enabled}
                                        />
                                        <span className={`text-sm ${!settings.completion_enabled ? 'text-slate-400' : 'text-slate-700'}`}>Notify Admin automatically</span>
                                    </label>
                                </div>

                                <button
                                    onClick={handleSaveSettings}
                                    disabled={isSaving}
                                    className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-slate-100 hover:bg-slate-200 text-slate-700 font-semibold rounded-lg transition-colors text-sm disabled:opacity-50 mt-4"
                                >
                                    {isSaving ? 'Saving...' : <><Save size={16} /> Save Automation Settings</>}
                                </button>
                            </div>

                            {/* Right Column: AI Reminders */}
                            <div>
                                <h4 className="text-sm font-semibold text-slate-800 uppercase tracking-wider mb-4 flex items-center gap-2">
                                    <Wand2 size={14} className="text-indigo-500" />
                                    AI-Suggested Reminders
                                </h4>
                                <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-xl p-5 border border-indigo-100 h-full flex flex-col">
                                    <p className="text-sm text-slate-600 mb-4 flex-1">
                                        Let AI analyze the current pending and overdue tasks to draft a professional, context-aware company notice reminding employees to complete their work.
                                    </p>

                                    {!suggestion ? (
                                        <button
                                            onClick={handleSuggestReminder}
                                            disabled={isSuggesting}
                                            className="w-full flex items-center justify-center gap-2 px-4 py-2.5 bg-indigo-600 hover:bg-indigo-700 text-white font-semibold rounded-lg transition-colors text-sm shadow-sm disabled:opacity-50"
                                        >
                                            {isSuggesting ? 'Drafting Notice...' : <><Wand2 size={16} /> Draft Smart Reminder</>}
                                        </button>
                                    ) : (
                                        <div className="bg-white rounded-lg p-4 shadow-sm border border-slate-200 animate-fadeIn">
                                            <div className="flex justify-between items-start mb-2">
                                                <h5 className="font-bold text-slate-800 text-sm pr-4">{suggestion.title}</h5>
                                                <button onClick={() => setSuggestion(null)} className="text-slate-400 hover:text-slate-600 text-xs">Clear</button>
                                            </div>
                                            <div
                                                className="text-sm text-slate-600 mb-4 prose prose-sm max-w-none prose-p:leading-snug"
                                                dangerouslySetInnerHTML={{ __html: suggestion.content }}
                                            />
                                            <button
                                                onClick={handleCreateNotice}
                                                disabled={isCreatingNotice}
                                                className="w-full flex items-center justify-center gap-2 px-4 py-2 bg-emerald-600 hover:bg-emerald-700 text-white font-semibold rounded-lg transition-colors text-sm shadow-sm disabled:opacity-50"
                                            >
                                                {isCreatingNotice ? 'Publishing...' : <><Bell size={16} /> Publish as Notice</>}
                                            </button>
                                        </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            )}
        </div>
    );
};

export default NotificationSettingsPanel;
